import { projectTotalScore } from "./coach-live-score";

/**
 * Score projection built from per-suggestion attributed gains.
 *
 * WHY THIS REPLACES PER-EDIT RE-SCORING: the previous approach asked the LMS to re-score the
 * whole résumé after every accept and hoped the number moved. Three problems, all fatal to the
 * requirement "accepting the AI-suggestions will increase the score as per the accepted changes":
 *
 *   1. The scorer is an LLM. It returns different sub-scores for the same input run to run, so a
 *      click could move the number by zero, or by something the click did not cause.
 *   2. It gave no reason. A student could not tell which suggestion earned which point, and a
 *      cosmetic rewrite scored identically to a substantive one — correctly, which made the
 *      whole panel look broken.
 *   3. It could not show a projection BEFORE anything was accepted, because nothing had changed
 *      yet. The design calls for "current → projected" up front.
 *
 * So the model now states, per suggestion, which component it improves and by how much (see
 * coach-plan.ts). This module turns those gains into scores. Accepting a suggestion adds exactly
 * its attributed gain — deterministic, instant, no network, and every point traceable.
 *
 * ⚠️ THE CEILING IS REAL AND MUST NOT BE HIDDEN. Only `skillOverlap` responds to a résumé edit;
 * `roleDomainFit`, `prs`, `experienceBandFit`, `ctcMatch` and `locationWorkModeFit` are folded
 * into `fixedContribution` by the LMS. For a student whose CTC expectation and location do not
 * match the job, those contribute nothing and the maximum achievable total is well below 100 —
 * 80 on the case this was built against. `projectTotalScore` clamps to the LMS's
 * `projectedCeiling` so the panel can never promise a number the model cannot produce.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */

/** The only component a résumé edit can move. Mirrors RESUME_EDITABLE_COMPONENTS. */
export type EditableComponent = "skillOverlap";

export const EDITABLE_COMPONENTS: EditableComponent[] = ["skillOverlap"];

export const isEditableComponent = (value: unknown): value is EditableComponent =>
  typeof value === "string" && (EDITABLE_COMPONENTS as string[]).includes(value);

/** What one suggestion claims it is worth. */
export type SuggestionGain = {
  targets: EditableComponent;
  /** Sub-score points added to `targets`, 0–100 before clamping. */
  gain: number;
};

/** Attributed gains keyed by suggestion id. */
export type SuggestionGains = Record<string, SuggestionGain>;

/**
 * NOT derived from `EditableComponent`/`Record<EditableComponent, number>` on purpose. `skillOverlap`
 * is the only component a résumé edit can move, but `roleDomainFit` still contributes a real,
 * pinned-at-baseline value to the weighted total (see `projectTotalScore`), so every baseline/totals
 * object here must still carry both fields even though only one is ever written to by `sumGains`.
 */
export type ComponentTotals = { roleDomainFit: number; skillOverlap: number };

export type ProjectionInputs = {
  /** Baseline sub-scores from the LMS, before any suggestion is applied. */
  baseline: ComponentTotals;
  /** Résumé-independent contribution, already weighted, from the handoff payload. */
  fixedContribution: number;
  weights: Record<string, number>;
  /** Highest total the model can produce for this student. */
  ceiling: number;
};

const emptyTotals = (): ComponentTotals => ({ roleDomainFit: 0, skillOverlap: 0 });

/**
 * Add up the gains of a set of suggestions, per component.
 *
 * Ignores unknown ids so a stale accepted-id list (from a previous run still in sessionStorage)
 * cannot inflate the score with gains that no longer exist.
 */
export const sumGains = (gains: SuggestionGains, suggestionIds: string[]): ComponentTotals => {
  const totals = emptyTotals();

  for (const id of suggestionIds) {
    const entry = gains[id];
    if (!entry || !isEditableComponent(entry.targets)) continue;

    const gain = Number(entry.gain);
    if (!Number.isFinite(gain) || gain <= 0) continue;

    totals[entry.targets] += gain;
  }

  return totals;
};

/**
 * Total score with a given set of gains applied on top of the baseline.
 *
 * Delegates the weighted sum and the ceiling clamp to `projectTotalScore`, which already mirrors
 * `jobMatcher.assembleScore` — so the projection and the authoritative scorer cannot drift apart
 * in how they combine components.
 */
export const scoreWithGains = (
  { baseline, fixedContribution, weights, ceiling }: ProjectionInputs,
  applied: ComponentTotals,
) =>
  projectTotalScore({
    fixedContribution,
    weights,
    // projectTotalScore clamps each sub-score to 100, so a model that over-claims a gain cannot
    // push a component past its maximum.
    roleDomainFit: baseline.roleDomainFit + applied.roleDomainFit,
    skillOverlap: baseline.skillOverlap + applied.skillOverlap,
    ceiling,
  });

export type ScoreProjection = {
  /** The LMS's score before this optimization run. The left-hand ring. */
  baseline: number;
  /** Baseline plus the gains of the suggestions accepted so far. The live number. */
  current: number;
  /** Baseline plus EVERY suggestion's gain. The right-hand ring, known before any click. */
  potential: number;
  /** Highest total achievable for this student, whatever the résumé says. */
  ceiling: number;
  /** Points the accepted suggestions have earned so far. */
  earned: number;
  /** Points still on the table. */
  remaining: number;
  acceptedCount: number;
  totalCount: number;
  perComponent: { accepted: ComponentTotals; all: ComponentTotals };
};

/**
 * Every id that can contribute score, across BOTH kinds of accept-able change.
 *
 * Exists because the two live in separate arrays — a `suggestion` replaces the text at a fieldPath,
 * a `skillAddition` appends a list item — and passing only `suggestions` to `buildScoreProjection`
 * silently understated `potential` and `totalCount`. With one summary rewrite and four skills on
 * offer, the panel said "accept all suggestions to reach 28%" when accepting all five reached 40,
 * while each skill card still advertised its own +3. Skill additions are the largest lever on
 * `skillOverlap`, so the omission dropped most of the available headroom.
 *
 * Kept as a named function so the invariant "both arrays count" is pinned by a test rather than
 * living in one line inside a React hook.
 */
export const collectAttributableIds = ({
  suggestions,
  skillAdditions,
}: {
  suggestions: Array<{ id: string }>;
  skillAdditions: Array<{ id: string }>;
}) => [...suggestions.map(({ id }) => id), ...skillAdditions.map(({ id }) => id)];

/**
 * Everything the score panel needs, from the plan plus the accepted set.
 *
 * `baseline` is the LMS's own number rather than a recomputation of it: quoting "was 37" from
 * anything other than the row the student's job card reads would let the two disagree.
 */
export const buildScoreProjection = ({
  inputs,
  gains,
  acceptedIds,
  allSuggestionIds,
  baselineScore,
}: {
  inputs: ProjectionInputs;
  gains: SuggestionGains;
  acceptedIds: string[];
  allSuggestionIds: string[];
  baselineScore: number;
}): ScoreProjection => {
  const accepted = sumGains(gains, acceptedIds);
  const all = sumGains(gains, allSuggestionIds);

  const current = scoreWithGains(inputs, accepted);
  const potential = scoreWithGains(inputs, all);

  return {
    baseline: baselineScore,
    current,
    potential,
    ceiling: inputs.ceiling,
    earned: Math.round((current - baselineScore) * 100) / 100,
    remaining: Math.round((potential - current) * 100) / 100,
    acceptedCount: acceptedIds.filter((id) => Boolean(gains[id])).length,
    totalCount: allSuggestionIds.length,
    perComponent: { accepted, all },
  };
};

/**
 * What accepting this one suggestion would add to the total, in its current context.
 *
 * Context-dependent on purpose: gains do not add linearly once a component is near 100 or the
 * total is at the ceiling, so the honest answer is the difference this click actually makes
 * rather than the raw `gain` the model claimed. Showing "+8" on a suggestion that would move the
 * total by 2 is the kind of small lie that makes a student stop trusting the panel.
 */
export const marginalGainOf = ({
  inputs,
  gains,
  acceptedIds,
  suggestionId,
}: {
  inputs: ProjectionInputs;
  gains: SuggestionGains;
  acceptedIds: string[];
  suggestionId: string;
}) => {
  if (!gains[suggestionId]) return 0;

  const withoutIt = acceptedIds.filter((id) => id !== suggestionId);
  const withIt = [...withoutIt, suggestionId];

  const before = scoreWithGains(inputs, sumGains(gains, withoutIt));
  const after = scoreWithGains(inputs, sumGains(gains, withIt));

  return Math.round((after - before) * 100) / 100;
};
